import { useToken } from "@gluestack-style/react";
import FormField from "components/form/FormField";
import Breadcrumbs from "components/general/Breadcrumbs";
import Container from "components/general/Container";
import DismissableContent from "components/general/DismissableContent";
import InfoBox from "components/general/InfoBox";
import ScrollContainer from "components/general/ScrollContainer";
import Section from "components/general/Section";
import { Box, Button, H1, HStack, Text } from "components/ui";
import { Formik } from "formik";
import { Check, CircleAlert } from "lucide-react-native";
import React from "react";
import * as Yup from "yup";

interface FormValues {
	site: string;
	machineManufacturer: string;
	machineType: string | null;
	machineModel: string | null;
	serialNumber: string | null;
	purchaseDate: string | null;
	serviceProvider: string | null;
	machineImage: any | null;
	helperText?: string;
}

const MACHINE_TYPE_OPTIONS = [
	{ value: "test1", label: "Test 1" },
	{ value: "test2", label: "Test 2" },
	{ value: "test3", label: "Test 3" },
];

const INITIAL_FORM_VALUES: FormValues = {
	site: "All",
	machineManufacturer: "",
	machineType: null,
	machineModel: null,
	serialNumber: null,
	purchaseDate: null,
	serviceProvider: null,
	machineImage: null,
};

const validationSchema = Yup.object().shape({
	site: Yup.string().required("Site is required"),
	machineManufacturer: Yup.string().required("Machine manufacturer required."),
	machineType: Yup.string().required("Machine type is required"),
	machineModel: Yup.string().required("Machine model is required"),
	serialNumber: Yup.string().required("Serial number is required"),
	// serviceProvider: Yup.string().required("Service provider is required"),
	// purchaseDate: Yup.string().required("Purchase date is required"),
	// machineImage: Yup.mixed().required("Machine image is required"),
});

export default function AddMachinePage() {
	const [errorMessages, setErrorMessages] = React.useState<string>("");
	const resolvedErrorBright = useToken("colors", "errorbright");
	const resolvedPrimaryLight = useToken("colors", "primarylight");
	const handleSubmit = async (values: FormValues, { resetForm }: any) => {
		try {
			// Implement submit logic here
			// await postMachine(values);
			resetForm();
			setErrorMessages("");
		} catch (error: any) {
			setErrorMessages(error?.data?.message || "An error occurred while saving the machine details.");
		}
	};

	return (
		<ScrollContainer>
			<Container>
				<Box
					sx={{
						paddingHorizontal: 16,
						"@md": { paddingHorizontal: 48 },
					}}
					paddingVertical={32}
				>
					<Box pb={20}>
						<Breadcrumbs />
					</Box>
				</Box>

				<Box alignItems="center">
					<Box flex={1} width="$full" maxWidth={1018}>
						<Section bg="$basicwhite">
							<Box gap={24} justifyContent="center" alignItems="center">
								<HStack w={"$full"} alignItems="center">
									<H1 sx={{ width: "fit-content" }}>Add a machine</H1>
									<Box ml={"auto"}>
										<Button flex={1} gap={6} variant="outline" borderRadius={6} onPress={() => {}}>
											<Check size={24} color={resolvedPrimaryLight} />
											<Text color="$primarylight">Save</Text>
										</Button>
									</Box>
								</HStack>
								<InfoBox icon="warning" text="All fields marked * are required." />
							</Box>
						</Section>

						<Section>
							<Formik initialValues={INITIAL_FORM_VALUES} validationSchema={validationSchema} onSubmit={handleSubmit}>
								{(formik) => (
									<Box gap={20}>
										{errorMessages && <DismissableContent icon={<CircleAlert color={resolvedErrorBright} />} text={errorMessages} variant="error" />}

										<FormField label="Site *" name="site" placeholder="Select a site..." formik={formik} helperText="Select the site where the machine will first be used" />

										<FormField label="Machine manufacturer" name="machineManufacturer" placeholder="Select make" formik={formik} helperText="Select the make of the machine. If it’s not in the list then select <Add>" />

										<FormField
											label="Machine type *"
											name="machineType"
											placeholder="Select type of machine"
											type="select"
											options={MACHINE_TYPE_OPTIONS}
											formik={formik}
											helperText="Select the type of machine. If it’s not in the list then select <Add>"
										/>

										<FormField
											label="Machine model *"
											name="machineModel"
											placeholder="Select type of machine"
											type="select"
											options={MACHINE_TYPE_OPTIONS}
											formik={formik}
											helperText="Select the model of the machine. If it’s not in the list then select <Add>"
										/>

										<FormField label="Serial No *" name="serialNumber" placeholder="Serial Number" formik={formik} helperText="Enter the serial no" />
										<FormField label="Purchase Date" name="purchaseDate" placeholder="Purchase date" type="date" formik={formik} helperText="Select when machine was purchased" />
										<FormField
											label="Service Provider"
											name="serviceProvider"
											placeholder="Select service provider"
											type="radio"
											options={MACHINE_TYPE_OPTIONS}
											formik={formik}
											helperText="Select the service provider. If it’s not in the list then select <Add>"
										/>
										<FormField label="Image" name="machineImage" placeholder="S" type="image" formik={formik} helperText="Select an image of the machine" />

										{/* <DateSelect /> */}
										<Box alignSelf="center">
											<Button flex={1} variant="primary" mt={20} borderRadius={6} onPress={() => formik.handleSubmit()}>
												<Text color="white">Save</Text>
											</Button>
										</Box>
									</Box>
								)}
							</Formik>
						</Section>
					</Box>
				</Box>
			</Container>
		</ScrollContainer>
	);
}
